import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardCheck, Search, Filter, Plus, Calendar, Clock, CheckCircle2,
  Eye, AlertTriangle, ChevronRight, FileText, Camera, PenTool,
  ArrowRight, PlayCircle, PauseCircle, Download, CalendarDays, User
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { formatDate, getInspectionStatusLabel, getInspectionStatusColor, getSeverityColor, getRiskLevelTextColor } from '../../utils/helpers';

export default function InspectionsPage() {
  const navigate = useNavigate();
  const { inspections, observations } = useAppStore();
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedInspection, setSelectedInspection] = useState<string | null>(null);

  const filtered = inspections.filter(i => {
    if (search && !i.vesselName.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    if (typeFilter !== 'all' && i.type !== typeFilter) return false;
    return true;
  });

  const detail = inspections.find(i => i.id === selectedInspection);
  const detailObs = observations.filter(o => o.inspectionId === selectedInspection);

  const statusCounts = {
    all: inspections.length,
    draft: inspections.filter(i => i.status === 'draft').length,
    in_progress: inspections.filter(i => i.status === 'in_progress').length,
    completed: inspections.filter(i => i.status === 'completed').length,
    under_review: inspections.filter(i => i.status === 'under_review').length,
    approved: inspections.filter(i => i.status === 'approved').length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Inspections</h1>
          <p className="text-sm text-slate-500 mt-1">Manage and conduct vessel inspections</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
          <button onClick={() => navigate('/app/inspections/conduct')} className="px-4 py-2 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 flex items-center gap-2 transition-colors shadow-md shadow-sky-500/20">
            <Plus className="w-4 h-4" /> New Inspection
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {[
          { key: 'all', label: 'All' },
          { key: 'draft', label: 'Draft' },
          { key: 'in_progress', label: 'In Progress' },
          { key: 'completed', label: 'Completed' },
          { key: 'under_review', label: 'Under Review' },
          { key: 'approved', label: 'Approved' },
        ].map(tab => (
          <button key={tab.key} onClick={() => setStatusFilter(tab.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
              statusFilter === tab.key
                ? 'bg-[#0A1628] text-white shadow-md'
                : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'
            }`}>
            {tab.label}
            <span className={`ml-2 text-xs ${statusFilter === tab.key ? 'text-slate-400' : 'text-slate-400'}`}>
              {statusCounts[tab.key as keyof typeof statusCounts]}
            </span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-100 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search inspections..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" />
          </div>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-sky-500">
            <option value="all">All Types</option>
            <option value="initial">Initial</option>
            <option value="periodic">Periodic</option>
            <option value="follow_up">Follow-up</option>
            <option value="unannounced">Unannounced</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Inspections List */}
        <div className="lg:col-span-2 space-y-3">
          {filtered.map(inspection => {
            const obs = observations.filter(o => o.inspectionId === inspection.id);
            return (
              <div key={inspection.id}
                onClick={() => setSelectedInspection(selectedInspection === inspection.id ? null : inspection.id)}
                className={`bg-white rounded-2xl border p-5 cursor-pointer transition-all hover:shadow-lg group ${
                  selectedInspection === inspection.id ? 'border-sky-500 shadow-lg shadow-sky-500/10 ring-1 ring-sky-500' : 'border-slate-100'
                }`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{inspection.vesselName}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{inspection.questionnaireName} · {inspection.type.replace('_', ' ')}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getInspectionStatusColor(inspection.status)}`}>
                    {getInspectionStatusLabel(inspection.status)}
                  </span>
                </div>

                {/* Progress Bar */}
                {(inspection.status === 'in_progress' || inspection.status === 'completed' || inspection.status === 'under_review' || inspection.status === 'approved') && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-slate-500">Progress</span>
                      <span className="font-medium text-slate-700">{inspection.progress}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-1000 ${
                        inspection.progress === 100 ? 'bg-emerald-500' : 'bg-sky-500'
                      }`} style={{ width: `${inspection.progress}%` }} />
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <CalendarDays className="w-3.5 h-3.5" />
                    <span>{formatDate(inspection.scheduledDate)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    <span>{inspection.inspectorName}</span>
                  </div>
                  {inspection.overallScore && (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="font-semibold text-slate-700">Score: {inspection.overallScore}/10</span>
                    </div>
                  )}
                  {obs.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      <span>{obs.length} observations</span>
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                {(inspection.status === 'completed' || inspection.status === 'approved' || inspection.status === 'under_review') && (
                  <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-slate-500">Pass: <strong className="text-slate-700">{inspection.passCount}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-slate-500">Fail: <strong className="text-slate-700">{inspection.failCount}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <div className="w-3 h-3 rounded-full bg-slate-300" />
                      <span className="text-slate-500">N/A: <strong className="text-slate-700">{inspection.naCount}</strong></span>
                    </div>
                  </div>
                )}

                {/* Expanded Details */}
                {selectedInspection === inspection.id && detail && (
                  <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                    {inspection.notes && (
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-xs font-medium text-slate-500 mb-1">Notes</p>
                        <p className="text-sm text-slate-700">{inspection.notes}</p>
                      </div>
                    )}

                    {detailObs.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-slate-500 mb-2">Observations ({detailObs.length})</p>
                        <div className="space-y-2">
                          {detailObs.map(obs => (
                            <div key={obs.id} className="bg-white border border-slate-100 rounded-xl p-3">
                              <div className="flex items-start justify-between mb-1">
                                <p className="text-sm font-medium text-slate-800">{obs.title}</p>
                                <div className="flex gap-1.5">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getSeverityColor(obs.severity)}`}>
                                    {obs.severity}
                                  </span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getRiskLevelTextColor(obs.riskLevel)}`}>
                                    {obs.riskLevel} risk
                                  </span>
                                </div>
                              </div>
                              <p className="text-xs text-slate-500 line-clamp-2">{obs.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button onClick={() => navigate(`/app/reports?inspectionId=${inspection.id}`)} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-xl text-sm font-medium hover:bg-sky-600 transition-colors flex items-center justify-center gap-2">
                        <Eye className="w-4 h-4" /> View Full Report
                      </button>
                      <button className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Export
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Side Panel - Inspection Actions */}
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4">Inspection Actions</h3>
            <div className="space-y-2">
              {[
                { icon: PlayCircle, label: 'Start New Inspection', desc: 'Begin a vessel inspection', color: 'bg-emerald-50 text-emerald-600' },
                { icon: ClipboardCheck, label: 'Continue Inspection', desc: 'Resume a draft inspection', color: 'bg-sky-50 text-sky-600' },
                { icon: FileText, label: 'Review & Approve', desc: 'Review completed inspections', color: 'bg-amber-50 text-amber-600' },
                { icon: Camera, label: 'Upload Evidence', desc: 'Add photos to observations', color: 'bg-purple-50 text-purple-600' },
                { icon: PenTool, label: 'Edit Report', desc: 'Modify inspection reports', color: 'bg-indigo-50 text-indigo-600' },
              ].map(action => (
                <button key={action.label} onClick={() => { if (action.label === 'Start New Inspection') navigate('/app/inspections/conduct'); }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors text-left group">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${action.color}`}>
                    <action.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 group-hover:text-sky-600 transition-colors">{action.label}</p>
                    <p className="text-xs text-slate-400">{action.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-sky-500 transition-colors" />
                </button>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {[
                { text: 'Marco Rossi started inspecting MV Nordic Star', time: '3 hours ago', color: 'bg-blue-500' },
                { text: 'James Mitchell completed MV Southern Grace inspection', time: '3 months ago', color: 'bg-emerald-500' },
                { text: 'Sarah Chen approved MV Pacific Titan report', time: '6 weeks ago', color: 'bg-green-500' },
              ].map((activity, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${activity.color}`} />
                  <div>
                    <p className="text-xs text-slate-600">{activity.text}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
